import { Link, Locator, LocatorLocations, LocatorText, Publication } from '@readium/shared'

/**
 * Readium Link のhrefから正規化されたファイル名を取得
 */
const normalizeHref = (href: string): string => {
  return href.split('#')[0].split('/').pop()?.toLowerCase() ?? ''
}

/**
 * TOCをフラット化して全アイテムのリストを返す
 */
export const flattenToc = (items: Link[]): Link[] => {
  const result: Link[] = []
  for (const item of items) {
    result.push(item)
    if (item.children && item.children.items.length > 0) {
      result.push(...flattenToc(item.children.items))
    }
  }
  return result
}

/**
 * TOCアイテムの総数を再帰的にカウントする
 */
export const countTocItems = (items: Link[]): number => {
  let count = 0
  for (const item of items) {
    count += 1
    if (item.children) {
      count += countTocItems(item.children.items)
    }
  }
  return count
}

/**
 * TOCからhref→タイトルのマップを構築する
 */
export const buildTocTitleMap = (toc: Link[]): Map<string, string> => {
  const tocMap = new Map<string, string>()
  const walk = (items: Link[]) => {
    for (const item of items) {
      if (item.title && item.href) {
        tocMap.set(item.href.split('#')[0], item.title)
      }
      if (item.children?.items.length) walk(item.children.items)
    }
  }
  walk(toc)
  return tocMap
}

/**
 * hrefからTOCタイトルを検索する（完全一致 → サフィックス一致のフォールバック）
 */
export const findTocTitleByHref = (href: string, tocMap: Map<string, string>): string | undefined => {
  const hrefClean = href.split('#')[0]
  const direct = tocMap.get(hrefClean)
  if (direct) return direct
  for (const [tocHref, title] of tocMap) {
    if (hrefClean.endsWith(tocHref) || tocHref.endsWith(hrefClean)) return title
  }
  return undefined
}

/** 検索結果の型 */
export type EpubSearchResult = {
  locator: Locator
  chapterTitle?: string
  /** body.textContent 内の絶対文字オフセット（精密スクロール用） */
  textOffset: number
}

/**
 * Publication の readingOrder をテキスト検索する共通ロジック
 * getTextForLink で各セクションのテキストを取得する（iframe DOM or fetch）
 */
export const searchPublication = async (
  pub: Publication,
  query: string,
  getTextForLink?: (link: Link) => Promise<string | null>,
): Promise<EpubSearchResult[]> => {
  if (!query.trim()) return []

  const tocMap = pub.toc?.items.length ? buildTocTitleMap(pub.toc.items) : new Map<string, string>()
  const queryLower = query.toLowerCase()
  const results: EpubSearchResult[] = []
  const parser = new DOMParser()

  for (const link of pub.readingOrder.items) {
    let text: string | null = null

    // カスタムテキスト取得関数があれば優先
    if (getTextForLink) {
      text = await getTextForLink(link)
    }

    // フォールバック: Publication経由でHTMLを取得
    if (text === null || text === undefined) {
      try {
        const html = await pub.get(link).readAsString()
        if (!html) continue
        const doc = parser.parseFromString(html, 'application/xhtml+xml')
        text = doc.body?.textContent ?? ''
      } catch {
        continue
      }
    }

    const textLower = text.toLowerCase()
    let searchStart = 0
    let chapterCount = 0

    while (chapterCount < 20) {
      const idx = textLower.indexOf(queryLower, searchStart)
      if (idx === -1) break

      const contextStart = Math.max(0, idx - 30)
      const contextEnd = Math.min(text.length, idx + query.length + 30)
      const before = text.slice(contextStart, idx)
      const highlight = text.slice(idx, idx + query.length)
      const after = text.slice(idx + query.length, contextEnd)
      const progression = idx / Math.max(text.length, 1)

      const locator = new Locator({
        href: link.href,
        type: link.mediaType?.string ?? 'application/xhtml+xml',
        locations: new LocatorLocations({ progression }),
        text: new LocatorText({ before, highlight, after }),
      })
      results.push({
        locator,
        chapterTitle: findTocTitleByHref(link.href, tocMap) ?? link.title ?? undefined,
        textOffset: idx,
      })
      searchStart = idx + query.length
      chapterCount++
    }
  }

  return results
}

/**
 * Locator のhrefに基づいてTOC内の章を特定する
 */
export const findChapterForLocator = (locator: Locator, toc: Link[]): Link | null => {
  if (!locator) return null

  const locatorFile = normalizeHref(locator.href)
  const flatToc = flattenToc(toc)

  // 後方から検索して最もマッチするTOCアイテムを見つける
  for (let i = flatToc.length - 1; i >= 0; i--) {
    const item = flatToc[i]
    const tocFile = normalizeHref(item.href)
    if (tocFile === locatorFile) return item
  }

  return null
}

/**
 * TOCリンクのhrefから推定ページ番号を計算する
 * positions（Locator[]）がある場合はそれに基づいて正確なページを算出
 */
export const getPageFromLink = (
  link: Link,
  positions: Locator[],
  numPages: number,
  tocIndex: number,
  totalTocItems: number,
): number | null => {
  if (numPages <= 0) return null

  if (positions.length > 0) {
    const linkFile = normalizeHref(link.href)

    // positionsの中からhrefが一致するLocatorを探す
    const matchedPosition = positions.find((pos) => normalizeHref(pos.href) === linkFile)

    if (matchedPosition?.locations.totalProgression != null) {
      return Math.max(1, Math.round(numPages * matchedPosition.locations.totalProgression))
    }
  }

  // フォールバック：TOCインデックスから推定
  if (totalTocItems > 0) {
    const percentage = tocIndex / totalTocItems
    return Math.max(1, Math.round(numPages * percentage))
  }

  return null
}

/**
 * Locator のtotalProgressionからページ番号を計算
 */
export const getPageFromLocator = (locator: Locator | null, numPages: number): number | null => {
  if (!locator || numPages <= 0) return null

  const progression = locator.locations.totalProgression
  if (progression != null) {
    return Math.max(1, Math.round(numPages * progression))
  }

  return null
}

/**
 * 現在のLocatorがどの章に属するかのhrefを返す
 */
export const getCurrentChapterHref = (currentLocator: Locator | null): string | null => {
  if (!currentLocator) return null
  return currentLocator.href
}
